"use client"

import { motion } from "framer-motion"
import { MapPin, Star } from "lucide-react"
import { Card, CardContent } from "@/components/ui/Card"
import { Badge } from "@/components/ui/Badge"

const experiences = [
  {
    title: "Senior Full Stack Developer",
    company: "TechCorp Solutions",
    period: "2022 - Present",
    location: "Remote",
    description:
      "Leading scalable web app development with React, Node.js, and AWS. Mentoring devs and building CI/CD pipelines.",
    achievements: [
      "Boosted performance by 40%",
      "Led 5-member team",
      "CI/CD automation",
    ],
    tech: ["React", "Node.js", "AWS", "Docker", "TypeScript"],
  },
  {
    title: "Frontend Developer",
    company: "Digital Innovations",
    period: "2021 - 2022",
    location: "New York, NY",
    description:
      "Built responsive UIs and collaborated with designers to deliver great UX.",
    achievements: [
      "15+ websites built",
      "60% higher engagement",
      "50% faster load times",
    ],
    tech: ["React", "Vue", "Sass", "JavaScript"],
  },
  {
    title: "Junior Web Developer",
    company: "StartupHub",
    period: "2020 - 2021",
    location: "San Francisco, CA",
    description:
      "Contributed to multiple projects and learned modern web dev practices.",
    achievements: ["20+ projects", "5+ tech stacks", "Open-source work"],
    tech: ["HTML", "CSS", "JavaScript", "PHP", "MySQL"],
  },
]

const ExperienceSection = () => (
  <section id="experience" className="py-20 relative">
    <div className="container mx-auto px-6">
      <motion.h2
        className="text-5xl font-bold text-center mb-16 text-white font-orbitron"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        Experience Journey
      </motion.h2>

      <div className="relative">
        {/* Vertical line */}
        <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-cyan-400 to-purple-600" />

        {experiences.map((exp, index) => (
          <motion.div
            key={index}
            className={`relative flex items-start mb-14 ${index % 2 === 0 ? "flex-row" : "flex-row-reverse"}`}
            initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className={`w-full lg:w-1/2 ${index % 2 === 0 ? "pr-6" : "pl-6"}`}>
              <Card className="bg-white/5 backdrop-blur-md border-cyan-500/20">
                <CardContent className="p-5 space-y-4">
                  <div className="flex justify-between">
                    <div>
                      <h3 className="text-xl font-bold text-white">{exp.title}</h3>
                      <p className="text-cyan-400">{exp.company}</p>
                    </div>
                    <div className="text-right text-sm text-gray-400">
                      <p>{exp.period}</p>
                      <p className="flex items-center justify-end">
                        <MapPin className="w-4 h-4 mr-1" />
                        {exp.location}
                      </p>
                    </div>
                  </div>

                  <p className="text-gray-300 text-sm">{exp.description}</p>

                  <ul className="text-sm space-y-1">
                    {exp.achievements.map((point, i) => (
                      <li key={i} className="flex items-center text-gray-300">
                        <Star className="w-3 h-3 text-yellow-400 mr-2" />
                        {point}
                      </li>
                    ))}
                  </ul>

                  <div className="flex flex-wrap gap-2 pt-2">
                    {exp.tech.map((tech) => (
                      <Badge key={tech} className="bg-purple-500/20 text-purple-400 border-purple-500/30">
                        {tech}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Timeline Dot */}
            <div className="absolute left-1/2 transform -translate-x-1/2 w-5 h-5 bg-gradient-to-br from-cyan-400 to-purple-600 rounded-full border-4 border-black z-10" />
          </motion.div>
        ))}
      </div>
    </div>
  </section>
)

export default ExperienceSection
